import React from 'react';
import type { RenderFn } from '@atlassian/forge-ui-types';
import { withConfluenceStyleWrapper } from '../confluenceStyleWrapper';
import { getPlotMargin, type MarginValue } from '../getPlotMargin';
import { fg } from '@atlaskit/platform-feature-flags';

const PlatformHorizontalBarChart = React.lazy(() =>
	// eslint-disable-next-line import/dynamic-import-chunkname
	import(
		/* webpackChunkName: '@forge-ui_horizontalbarchart' */
		'@atlassian/viz-platform-charts/charts'
	).then((module) => ({ default: module.HorizontalBarChart })),
);

const Title = React.lazy(() =>
	// eslint-disable-next-line import/dynamic-import-chunkname
	import(
		/* webpackChunkName: '@forge-ui_horizontalbarchart' */
		'@atlassian/viz-platform-charts/charts'
	).then((module) => ({ default: module.HorizontalBarChart.elements.Title })),
);

const SubTitle = React.lazy(() =>
	// eslint-disable-next-line import/dynamic-import-chunkname
	import(
		/* webpackChunkName: '@forge-ui_horizontalbarchart' */
		'@atlassian/viz-platform-charts/charts'
	).then((module) => ({ default: module.HorizontalBarChart.elements.SubTitle })),
);

const Layout = React.lazy(() =>
	// eslint-disable-next-line import/dynamic-import-chunkname
	import(
		/* webpackChunkName: '@forge-ui_barchart' */
		'@atlassian/viz-platform-charts/charts'
	).then((module) => ({ default: module.HorizontalBarChart.elements.Layout })),
);

type PlatformHorizontalBarChartProps = React.ComponentProps<typeof PlatformHorizontalBarChart>;

/**
 * Excluded props:
 * - children - we will not be supporting primitives/elements to compose the chart
 *
 * Limited props:
 * - xAccessor - only allow non function types
 * - yAccessor - only allow non function types
 * - colorAccessor - only allow non function types
 */

export type HorizontalBarChartProps = Pick<
	PlatformHorizontalBarChartProps,
	'width' | 'height' | 'data'
> & {
	colorAccessor?: number | string;
	margin?: MarginValue;
	marginBottom?: MarginValue;
	marginLeft?: MarginValue;
	marginRight?: MarginValue;
	marginTop?: MarginValue;
	subtitle?: string;
	title?: string;
	xAccessor: number | string;
	yAccessor: number | string;
};

const BaseHorizontalBarChart = (props: Parameters<RenderFn>[0]) => {
	const {
		width,
		height,
		data,
		xAccessor,
		yAccessor,
		colorAccessor,
		title,
		subtitle,
		margin,
		marginTop,
		marginRight,
		marginBottom,
		marginLeft,
	} = props.forgeDoc.props as HorizontalBarChartProps;

	return (
		<PlatformHorizontalBarChart
			width={width}
			height={height ? height : 400}
			data={data}
			xAccessor={xAccessor}
			yAccessor={yAccessor}
			colorAccessor={colorAccessor}
		>
			{title && <Title>{title}</Title>}
			{subtitle && <SubTitle>{subtitle}</SubTitle>}
			{fg('enable-margin-property-for-ui-kit-chart-components') && (
				<Layout
					plotMargin={getPlotMargin(margin, marginLeft, marginRight, marginTop, marginBottom)}
				/>
			)}
		</PlatformHorizontalBarChart>
	);
};

export const HorizontalBarChart: (props: Parameters<RenderFn>[0]) => JSX.Element =
	withConfluenceStyleWrapper(BaseHorizontalBarChart);
